import React, { useState } from 'react';
import { Box, Stack, Typography, Chip, LinearProgress, IconButton } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useTranslation } from 'react-i18next'; 
import PreviewWord from '../PIIAnonymizerPage/PIIFileUploadPanel/PreviewWord'; 

const statusColor = (status) => {
  if (status === 'success') return 'success';
  if (status === 'error') return 'error';
  if (status === 'uploading') return 'warning';
  return 'default';
};

const getStatusLabel = (status, t) => {
  if (status === 'success') return t('common.done', 'Hotovo');
  if (status === 'error') return t('common.error', 'Chyba');
  if (status === 'uploading') return t('common.processing', 'Spracovávam');
  return t('common.ready', 'Pripravené');
};

const FileCard = ({ fileItem, isUploading, onDownload, onRemove }) => {
  const { t } = useTranslation();
  
  // Local state to manage the preview modal visibility
  const [previewOpen, setPreviewOpen] = useState(false);

  if (!fileItem) return null;

  const canDownload = fileItem.status === 'success' && fileItem.blob;
  
  // Ensure we only show the preview icon for Word documents
  const isDocx = (fileItem.name || '').toLowerCase().endsWith('.docx');

  return (
    <Box
      sx={{
        mb: 2,
        p: 1.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: fileItem.status === 'error' ? 'error.light' : 'divider',
        bgcolor: 'action.hover', // Using action.hover for better contrast inside panels
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Box sx={{ minWidth: 0, mr: 1, flex: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap title={fileItem.name}>
            {fileItem.name}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center">
          <Chip 
            label={getStatusLabel(fileItem.status, t)} 
            size="small" 
            color={statusColor(fileItem.status)} 
            sx={{ borderRadius: 1.5 }}
          />

          {/* Show preview icon only if processing is done and it's a DOCX */}
          {canDownload && isDocx && (
            <IconButton size="small" onClick={() => setPreviewOpen(true)} sx={{ bgcolor: 'background.paper' }}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          )}

          {canDownload && (
            <IconButton size="small" onClick={onDownload} sx={{ bgcolor: 'background.paper' }}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          )}

          <IconButton size="small" onClick={onRemove} disabled={isUploading} color="error" sx={{ bgcolor: 'background.paper' }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      <LinearProgress 
        variant={fileItem.status === 'uploading' && fileItem.progress === 100 ? "indeterminate" : "determinate"}
        value={fileItem.progress} 
        sx={{ borderRadius: 1, height: 6 }}
      />

      {fileItem.error && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block', fontWeight: 600 }}>
          {fileItem.error}
        </Typography>
      )}

      {/* Render the preview modal if triggered */}
      {previewOpen && isDocx && (
        <PreviewWord 
          open={previewOpen}
          fileItem={fileItem}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </Box>
  );
};

export default FileCard;